import React, { useState, useEffect, useRef } from "react";
import { Box, DialogContent, Typography, Checkbox, FormControlLabel, useTheme, useMediaQuery } from "@mui/material";
import DialogComponent from "../../atom/Dialog/Dialog";
import TextButtonComponet from "../../atom/Buttons/TextButton";
import { Document, Page } from "react-pdf";

const TermsAndConditionsDialog = ({ open, onAccept, onClose }) => {
  const [isAccepted, setIsAccepted] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [containerWidth, setContainerWidth] = useState(600);
  const pdfContainerRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Reset acceptance when dialog open
  useEffect(() => {
    if (open) {
      setIsAccepted(false);
      setNumPages(null)
    }
  }, [open]);

  useEffect(() => {
    function updateWidth() {
      if (pdfContainerRef.current) {
        setContainerWidth(pdfContainerRef.current.offsetWidth);
      }
    }
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const handleCheckboxChange = (event) => {
    setIsAccepted(event.target.checked);
  };

  const handleAccept = () => {
    if (isAccepted) {
      onAccept();
    }
  };

  return (
    <DialogComponent
      open={open}
      onClose={() => {}} // Prevent closing without acceptance
      title="Terms and Conditions"
      maxwidth="lg"
      student={true}
      isShowCloseButton={false}
      backgroundColor="#9834F0"
    >
      <DialogContent
        sx={{
          padding: { xs: 1, sm: 2, md: 3 },
          "&.MuiDialogContent-root": {
            padding: { xs: 1, sm: 2, md: 3 },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: { xs: "80vh", sm: "70vh" },
            maxHeight: { xs: "80vh", sm: "70vh", md: "600px" },
          }}
        >
          {/* PDF Viewer Section - Single Scroll Container */}
          <Box
            sx={{
              flex: 1,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              backgroundColor: "#ffffff",
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: "100%",
                minHeight: "300px",
                maxHeight: { xs: "60vh", sm: "70vh", md: "600px" },
                border: "none",
                borderRadius: isMobile ? 0 : "8px",
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                alignItems: isMobile ? "flex-start" : "center",
                overflowX: isMobile ? "auto" : "hidden",
                WebkitOverflowScrolling: "touch",
                justifyContent: "flex-start",
                background: "#fff",
                padding: { xs: 1, sm: 2 },
              }}
              ref={pdfContainerRef}
            >
              <Document
                key={open ? "open" : "closed"} 
                file="/terms-and-conditions.pdf"
                onLoadSuccess={({ numPages }) => setNumPages(numPages)}
              >

                {numPages && numPages > 0 &&
                  Array.from({ length: numPages }, (_, index) => (
                    <Page
                      pageNumber={index + 1}
                      width={containerWidth - (isMobile ? 0 : 32)}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      style={{ margin: "0 auto" }}
                    />

                  ))}
              </Document>
            </Box>
          </Box>

          {/* Fixed Footer Section */}
          <Box
            sx={{
              borderTop: "2px solid #e0e0e0",
              backgroundColor: "#ffffff",
              p: { xs: 2, sm: 3 },
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isAccepted}
                    onChange={handleCheckboxChange}
                    color="primary"
                    sx={{
                      color: "#9834F0",
                      "&.Mui-checked": {
                        color: "#9834F0",
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontFamily: "Montserrat",
                      fontSize: { xs: "13px", sm: "15px" },
                      color: "#333",
                      fontWeight: 500,
                    }}
                  >
                    I have read and agree to the Terms and Conditions
                  </Typography>
                }
              />
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <TextButtonComponet
                text="I Agree"
                classStyle="btn btn-primary"
                onButtonClick={handleAccept}
                isDisabled={!isAccepted}
                style={{
                  minWidth: isMobile ? "100%" : "200px",
                  padding: isMobile ? "10px 0" : "12px 24px",
                  fontSize: isMobile ? "15px" : "16px",
                  fontWeight: 600,
                  opacity: isAccepted ? 1 : 0.5,
                  cursor: isAccepted ? "pointer" : "not-allowed",
                }}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </DialogComponent>
  );
};

export default TermsAndConditionsDialog;

